import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, FileTextIcon, FolderSearchIcon, LayersIcon, LayoutGridIcon, TargetIcon, UsersIcon, ShieldCheckIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
export const BRAND = 'NATURKLAR™';
export const heroGuarantees = ['Øjeblikkelig digital adgang', 'Engangsbetaling', 'Intet abonnement', '7 dages tilfredshedsgaranti'];
export const stats = [{
  value: '240',
  label: 'Sider i de seks bind'
}, {
  value: '6',
  label: 'Digitale bind'
}, {
  value: '20',
  label: 'Sikre forsøg med billige hverdagsmaterialer'
}, {
  value: '5',
  label: 'Bonusser med i pakken'
}, {
  value: '3',
  label: 'Niveauer i elevarkene'
}];
export const differenceStats = [{
  value: '1.–6. klasse',
  label: 'Natur/teknologi fra indskoling til mellemtrin'
}, {
  value: '6 digitale bind',
  label: 'Fra levende natur, fysik og teknologi til stof, vand, vejr og Jorden'
}, {
  value: 'PDF klar til print',
  label: 'Brug materialet digitalt eller print det til klassen'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Aftener og weekender forsvinder',
  text: 'Timevis foran skærmen med at planlægge næste uges natur/teknologi-timer – og arbejdet er stadig langt fra færdigt.'
}, {
  title: 'Endeløs jagt på forsøg',
  text: 'Forsøgene fra forskellige hjemmesider kræver dyre materialer, virker ikke i praksis eller passer ikke til klassetrinnet.'
}, {
  title: 'Store forskelle i klassen',
  text: 'Nogle elever skal have hjælp til at læse opgaven, andre er færdige på fem minutter – og timen skal fungere for alle.'
}, {
  title: 'Uro, når forsøgene starter',
  text: 'Uden klare roller, regler og materialelister bliver forsøgstimen let til kaos i stedet for undersøgelse.'
}, {
  title: 'Evaluering og dokumentation tager tid',
  text: 'Kompetencemål efter 2., 4. og 6. klasse, tegn på læring og elevplaner – hvordan dokumenterer du det hele?'
}, {
  title: 'Stress når timen begynder',
  text: 'Når tiden løber fra dig, bliver timen let til »læs side 12 i bogen« og improvisation i sidste øjeblik.'
}];
export const afterItems = ['Færdige årsplaner for 1.–2., 3.–4. og 5.–6. klasse', '12 færdige lektioner minut for minut', 'Mål, materialer, sikkerhed og lærerens instruktion til hver lektion', 'Elevark i 3 niveauer', '20 sikre forsøg med billige hverdagsmaterialer', 'Bygget på Fælles Mål for natur/teknologi', 'Undersøgelse, modellering, perspektivering og kommunikation', 'Tests A/B med facit og evalueringsskema', 'PDF-filer klar til print'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Aftenerne går med planlægning',
  text: 'Bruger du aftener og weekender på at planlægge næste uges natur/teknologi?'
}, {
  icon: FolderSearchIcon,
  title: 'Du leder efter forsøg, der virker',
  text: 'Leder du hele tiden på nettet efter forsøg, der er sikre, billige og passer til klassetrinnet?'
}, {
  icon: UsersIcon,
  title: 'Store forskelle i klassen',
  text: 'Har du brug for elevark, der virker både for elever, der skal have støtte, og for elever, der skal udfordres?'
}, {
  icon: TargetIcon,
  title: 'Du underviser ikke kun i dit eget fag',
  text: 'Er du klasselærer og har fået natur/teknologi på skemaet uden at have faget som linjefag?'
}, {
  icon: ClipboardCheckIcon,
  title: 'Evaluering tager tid',
  text: 'Vil du gerne have færdige tests, tjeklister til kompetencemålene og feedback, du kan bruge med det samme?'
}, {
  icon: LayoutGridIcon,
  title: 'Du savner en klar struktur',
  text: 'Vil du gerne have alt samlet ét sted, så du hurtigt finder den rigtige lektion, når timen starter?'
}];

// Spine colours mirror the physical box (blue, light blue, green, yellow, orange, red).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Bind 01',
  level: '1.–6. klasse',
  title: 'Årsplan og 12 lektioner minut for minut',
  tagline: 'Du ved præcis, hvad der skal ske i hver time – hele skoleåret.',
  intro: 'Første bind giver dig færdige årsplaner for 1.–2., 3.–4. og 5.–6. klasse og tolv færdige lektioner, som du kan gennemføre med det samme.',
  items: ['Årsplaner for 1.–2., 3.–4. og 5.–6. klasse (40 uger)', '12-ugersplan og progressionstabel', 'Kompetencemål efter 2., 4. og 6. klasse', 'Sikkerhed i natur/teknologi', '12 lektioner på 45 minutter, minut for minut', 'Mål, materialer, sikkerhed og lærerens instruktion', 'Elevark i 3 niveauer til hver lektion', '2 tests (1.–3. og 4.–6. klasse) med facit', 'Evalueringsskema'],
  outro: 'Fra »Flyder eller synker?« til »Design en vindmølle« – klar til klassen.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Bind 02',
  level: '1.–6. klasse',
  title: 'Levende natur: planter, dyr, økosystemer og kroppen',
  tagline: 'Gør naturen omkring eleverne levende og forståelig.',
  intro: 'Andet bind tager eleverne fra frø og planter til dyr, levesteder, fødekæder og kroppen.',
  items: ['Planter og fotosyntese (enkelt forklaret)', 'Dyr og levesteder', 'Livscyklusser', 'Fødekæder', 'Årstider i naturen', 'Kroppen og sundhed', 'Biodiversitet og bæredygtighed', 'Elevark i 3 niveauer med facit'],
  outro: 'Natur, som eleverne kan se, røre og undersøge.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 03',
  level: '1.–6. klasse',
  title: 'Fysik og teknologi: kræfter, energi, lys, lyd og elektricitet',
  tagline: 'Fra simple maskiner til egne konstruktioner.',
  intro: 'Tredje bind gør fysik og teknologi konkret med forsøg, modeller og design- og konstruktionsopgaver.',
  items: ['Kræfter og bevægelse', 'Simple maskiner', 'Energiformer', 'Lys og skygge', 'Lyd', 'Strømkredse', 'Magnetisme', 'Design- og konstruktionsopgaver'],
  outro: 'Teknologi, eleverne selv bygger og afprøver.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Bind 04',
  level: '1.–6. klasse',
  title: 'Stof, vand, vejr og Jorden',
  tagline: 'Fra en vanddråbe til solsystemet.',
  intro: 'Fjerde bind handler om stoffer, vandets kredsløb, vejr og klima, Jorden og universet – og om lokalområdet.',
  items: ['Stoffer og blandinger', 'Tilstandsformer', 'Vandets kredsløb', 'Luft, vejr og klima', 'Jorden, solsystemet og universet', 'Ressourcer, affald og genbrug', 'Kort og lokalområdet', 'Elevark i 3 niveauer med facit'],
  outro: 'Store spørgsmål – forklaret, så eleverne forstår dem.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 05',
  level: '1.–6. klasse',
  title: 'Forsøg, undersøgelser og modeller',
  tagline: 'Undersøgelser, der virker – uden dyrt udstyr.',
  intro: 'Femte bind samler 20 sikre forsøg med billige hverdagsmaterialer og skabeloner, der lærer eleverne at undersøge som naturforskere.',
  items: ['20 sikre forsøg med billige hverdagsmaterialer', 'Skabeloner til forsøgsrapport', 'Spørgsmål – forventning/hypotese – forsøg – konklusion', 'Modellering', 'Sikkerhedsregler', 'Progression fra 1. til 6. klasse'],
  outro: 'Forsøgstimer med ro, struktur og læring.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 06',
  level: '1.–6. klasse',
  title: 'Evaluering og brobygning til udskolingen',
  tagline: 'Se, hvad eleverne kan – og byg bro til fysik/kemi, biologi og geografi.',
  intro: 'Sjette bind samler tests, tegn på læring og tjeklister til kompetencemålene efter 2., 4. og 6. klasse.',
  items: ['Tests A/B pr. emne med facit', 'Tegn på læring pr. kompetenceområde', 'Kompetencemål efter 2., 4. og 6. klasse som tjeklister', 'Selv- og makkerevaluering', '100 færdige feedbackkommentarer', 'Brobygning: fra natur/teknologi til fysik/kemi, biologi og geografi'],
  outro: 'Fuld støtte til en retfærdig evaluering – hele vejen til 6. klasse.',
  note: 'NATURKLAR™ er udviklet uafhængigt og er ikke en officiel udgivelse fra Børne- og Undervisningsministeriet.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 digitale bind i natur/teknologi',
  text: 'Et komplet undervisningssystem, der hjælper dig med at planlægge og gennemføre natur/teknologi hele skoleåret. Hvert bind har 40 sider med færdige lektioner, elevark i 3 niveauer og facit.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Færdige lektioner minut for minut',
  text: 'Slut med det blanke papir. Klare lektionsplaner på 45 minutter med mål, materialer, sikkerhed og lærerens instruktion.'
}, {
  icon: FileTextIcon,
  title: 'Elevark i 3 niveauer',
  text: 'Samme mål i tre niveauer – støtte, basis og udfordring – så alle elever kan arbejde med det samme emne.'
}, {
  icon: ShieldCheckIcon,
  title: '20 sikre forsøg',
  text: 'Forsøg med billige hverdagsmaterialer, materialelister og klare sikkerhedsregler – klar til klasselokalet.'
}, {
  icon: LayersIcon,
  title: 'Bygget på Fælles Mål',
  text: 'Undersøgelse, modellering, perspektivering og kommunikation – med kompetencemål efter 2., 4. og 6. klasse.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Evaluering',
  text: 'Tests A/B med facit, tegn på læring, tjeklister og 100 færdige feedbackkommentarer.'
}, {
  icon: CalendarCheckIcon,
  title: 'Årsplan',
  text: 'Færdige årsplaner for 40 uger, så hele skoleåret i natur/teknologi hænger sammen.'
}];
// Real pages from Bind 1 are added later (data/pages.ts). While that list is empty,
// the carousel is hidden and InsidePages shows the mockup with the Bind 1 contents.
export const pageSamples: Array<{
  src: string;
  label: string;
  title: string;
  text: string;
}> = [];
export const pageTypes = ['Færdige lektioner', 'Elevark i 3 niveauer', 'Sikre forsøg', 'Facit', 'Evaluering'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Natur/teknologi-start på 5 minutter',
  text: 'Korte opstarter, der får eleverne til at tænke naturfagligt fra første minut.',
  items: ['40 korte opstarter', 'Gåder og »hvad sker der, hvis …?«', 'Billedspørgsmål og minidemonstrationer', 'Facit følger med'],
  benefit: 'Hver time starter med nysgerrighed.'
}, {
  title: 'Udeskole i natur/teknologi: 30 færdige udendørslektioner',
  text: 'Tag undervisningen med ud – med færdige forløb, du kan bruge i skolegården, parken eller skoven.',
  items: ['30 udendørslektioner', 'Naturtur og insektjagt', 'Vejrstation og skyggemåling', 'Planteherbarium og artskort'],
  benefit: 'Naturen bliver klasselokalet.'
}, {
  title: 'Skole–hjem-kommunikation',
  text: 'Færdige tekster, så forældrene altid ved, hvad der sker i natur/teknologi.',
  items: ['Færdige Aula-beskeder', 'Ugebreve', 'Info om forsøg og ture', 'Skabelon til skole-hjem-samtale'],
  benefit: 'Tryg kommunikation – uden at skrive fra bunden.'
}, {
  title: 'Vikarmappen til natur/teknologi',
  text: 'Lektioner, som en vikar kan gennemføre uden forberedelse.',
  items: ['15 færdige lektioner', 'Instruktion til vikaren', 'Elevark med facit', 'Klar til print'],
  benefit: 'Ingen spildte timer, når du er fraværende.'
}, {
  title: 'Plakater til natur/teknologi',
  text: 'Plakater til klasselokalet, der støtter de vigtigste begreber og rutiner.',
  items: ['20 plakater', 'Sikkerhedsregler og undersøgelsens 5 trin', 'Kroppens organer, vandets kredsløb og planeterne', 'Fødekæde og ordliste'],
  benefit: 'Et klasselokale, der underviser med.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Åbn NATURKLAR™',
  text: 'Hent pakken og åbn det bind, du skal bruge.'
}, {
  title: 'Vælg klasse og lektion',
  text: 'Find den lektion, det forsøg eller det elevark, der passer til din klasse.'
}, {
  title: 'Forbered dig på 5 minutter',
  text: 'Læs lærerens instruktion og find materialerne frem – listen står på siden.'
}, {
  title: 'Print og start timen',
  text: 'Print elevarkene eller brug materialet digitalt – og gå ind til timen med en klar plan.'
}];
export const audienceItems = ['Du underviser i natur/teknologi i 1.–6. klasse', 'Du er klasselærer og har natur/teknologi på skemaet', 'Du har ikke natur/teknologi som linjefag', 'Du vil have en færdig årsplan bygget på Fælles Mål', 'Du vil have sikre forsøg med billige materialer', 'Du vil have elevark i 3 niveauer med facit', 'Du vil have hjælp til evaluering og kompetencemål', 'Du vil have dine aftener og weekender tilbage'];
export const timeValueTasks = ['At lede efter forsøg på nettet', 'At planlægge lektioner', 'At lave elevark i flere niveauer', 'At skrive materialelister og sikkerhedsregler', 'At evaluere og give feedback'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 digitale bind – 240 sider',
  detail: 'Årsplan og lektioner, levende natur, fysik og teknologi, stof, vand og Jorden, forsøg og evaluering'
}, {
  label: '5 bonusser uden ekstra betaling',
  detail: 'Natur/teknologi-start på 5 minutter, Udeskole, Skole–hjem-kommunikation, Vikarmappen og Plakater'
}, {
  label: 'Til 1.–6. klasse',
  detail: 'Færdige lektioner, elevark i 3 niveauer, 20 sikre forsøg, facit og evalueringsskema'
}];
export const offerIncludes = ['Øjeblikkelig digital adgang', 'PDF-filer du kan gemme', 'Klar til print', 'Intet fysisk produkt sendes'];
export const PRICE = '199 kr.';
export const PRICE_BEFORE = '399 kr.';

// Only add authentic reviews of NATURKLAR. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'Hvordan får jeg materialet?',
  a: 'NATURKLAR™ leveres digitalt. Efter købet får du adgang til PDF-filerne, som du kan hente og gemme. Du kan bruge materialet på computer, tablet eller mobil og printe de sider, du har brug for.'
}, {
  q: 'Følger materialet Fælles Mål?',
  a: 'Ja. NATURKLAR™ er bygget på Fælles Mål for natur/teknologi og de fire kompetenceområder Undersøgelse, Modellering, Perspektivering og Kommunikation, med kompetencemål efter 2., 4. og 6. klassetrin. NATURKLAR™ er udviklet uafhængigt og er ikke en officiel udgivelse fra Børne- og Undervisningsministeriet.'
}, {
  q: 'Er det klar til den nye fagplan?',
  a: 'Den nye fagplan for natur/teknologi er stadig et udkast og ventes at gælde fra skoleåret 2027/28. NATURKLAR™ arbejder allerede med undersøgelse, modellering, perspektivering og kommunikation – kernen i faget – så materialet kan bruges nu og i overgangen til den nye fagplan.'
}, {
  q: 'Hvilke klassetrin passer materialet til?',
  a: 'Materialet dækker 1.–6. klasse. Årsplanerne er delt op i 1.–2., 3.–4. og 5.–6. klasse, og hver lektion er markeret med klassetrin. Du vælger selv lektioner, forsøg og elevark efter din klasse.'
}, {
  q: 'Er forsøgene sikre, og hvad skal jeg bruge?',
  a: 'Forsøgene er valgt, så de kan laves sikkert i et almindeligt klasselokale med billige hverdagsmaterialer. Hver lektion har en materialeliste og et sikkerhedsafsnit, og Bind 5 samler sikkerhedsreglerne.'
}, {
  q: 'Kan jeg virkelig forberede en lektion på 5 minutter?',
  a: 'NATURKLAR™ er lavet til at gøre forberedelsen hurtig. Du åbner et bind, vælger en færdig lektion og læser lærerens instruktion igennem på cirka fem minutter. Tiden kan variere efter lektionen og de materialer, der skal findes frem.'
}, {
  q: 'Rækker materialet til hele skoleåret?',
  a: 'NATURKLAR™ indeholder årsplaner for 40 uger og færdige lektioner og forløb i alle bind. Du vælger, kombinerer og tilpasser materialet efter din egen plan og dine elevers behov.'
}, {
  q: 'Er der elevark i forskellige niveauer?',
  a: 'Ja. Elevarkene findes i 3 niveauer – støtte, basis og udfordring – så elever med forskellige behov kan arbejde mod det samme mål.'
}, {
  q: 'Følger der facit med?',
  a: 'Ja. Der er facit til elevark, opgaver og tests, så du sparer tid på rettearbejdet.'
}, {
  q: 'Er evaluering inkluderet?',
  a: 'Ja. Bind 6 indeholder tests A/B med facit, tegn på læring pr. kompetenceområde, tjeklister til kompetencemålene efter 2., 4. og 6. klasse, selv- og makkerevaluering og 100 færdige feedbackkommentarer.'
}, {
  q: 'Skal jeg printe det hele?',
  a: 'Nej. Du vælger selv, hvilke sider du printer, og du kan også bruge dele af materialet digitalt.'
}, {
  q: 'Er de fem bonusser inkluderet i prisen?',
  a: `Ja. Alle fem bonusser er med i pakken uden ekstra betaling – i alt ${PRICE}.`
}, {
  q: 'Er det et abonnement?',
  a: 'Nej. Du betaler én gang og får adgang til det digitale materiale. Ingen løbende betalinger.'
}, {
  q: 'Er der en garanti?',
  a: 'Ja. NATURKLAR™ har 7 dages tilfredshedsgaranti. Er du mod forventning utilfreds med materialet, kan du kontakte os og få pengene tilbage inden for garantiperioden efter betingelserne.'
}];
export const footerLinks = ['Handelsbetingelser', 'Privatlivspolitik', 'Returnering', 'Kontakt'];
